import { gql } from '@apollo/client'

/**
 * TileV2 / SectionGraphQLManager content document. Fast for one spotlight id;
 * 12+ ids block Member Home TTFB (~3.4s on stage). SSR no longer seeds tile
 * rows with this query.
 *
 * @see .cursor/plans/brooklyn-get-content-tile-ssr.md
 */
export const GET_CONTENT = gql`
  query GetContent (
        $titleGroupIds: [String]!,
        $language: [String]!,
        $primaryLanguage: [String]!,
        $imageType: [String]!,
        $limitedData: Boolean!,
        $includeSeriesImage: Boolean!
        $includeReferenceImage: Boolean = false
        $includeCourseProgress: Boolean = false
        $learnerLevelLanguage: String!
    ) {
    contentList(
        where: {
            titleGroupId: { in: $titleGroupIds }
        }
        language: $language
    ) {
        content {
            contentAnalytic {
                commentsCountTotal
                votesUpCountTotal
                votesCountTotal
                thumbPopularityMappingId
            }
            id
            title
            path
            episodeNumber
            seasonNumber
            displayType
            studio
            contentType
            subType
            contentId
            titleGroupId
            seasonsCount (language: $primaryLanguage)
            episodesCount (language: $primaryLanguage, content: [{ publishedState: PUBLISHED_NOW }])
            isNew
            primaryLanguage
            languageFullText
            cast
            instructor
            published
            createdAt
            shortDescription
            description (format: HTML) @skip(if: $limitedData)
            ctaLabel
            ctaPath
            productionStatus
            references (types: ["genres", "centers", "yoga-duration", "yoga-style", "yoga-level", "fitness-duration", "fitness-style", "fitness-level", "meditation-duration", "meditation-style", "meditation-level", "primary-course", "full-video"]) {
                id
                contentReferenceType {
                    type
                }
                contentTo (language: $language) {
                    title
                    contentType
                    contentId
                    language
                    path
                    imageContentRelationships (
                        language: $language
                        types: $imageType
                    ) @include(if: $includeReferenceImage) {
                        type
                        image {
                            url
                        }
                    }
                }
            }
            featureMedia {
                duration
                mediaId
                mediaAssets {
                    language
                    mediaReferenceType
                    aiMetadata(language: $learnerLevelLanguage) {
                        learnerLevel {
                            level
                        }
                    }
                }
                contentPolicy {
                    shareAllowed
                    accessCheck {
                        allowed
                        countryAllowed
                        subscriptionsAllowed
                        isFreeAllowed
                    }
                    optionIds
                }
                userMediaPosition {
                    position
                }
                mediaAssetsVideoPlayback(
                    hlsVersion: ["7", "4"]
                    language: $language
                ) {
                    previewHlsUrl
                }
            }
            previewMedia {
                mediaId
            }
            imageContentRelationships (
                language: $language
                types: $imageType
            ) {
                type
                image {
                    url
                }
            }
            contentAssetReferences (contentAssetReferences: {type: "worksheet"}) @skip(if: $limitedData) {
                asset {
                    originalFileName
                    language
                    renderUrl
                    mimeType
                    uuid
                }
                contentAssetReferenceType {
                    type
                }
            }
            contentReferencesFrom (types: ["product"]) @skip(if: $limitedData) {
                contentTo {
                    contentId
                    contentType
                    title
                    ctaProductUrl
                    ctaLabel
                    imageContentRelationships (
                        language: $language
                        types: $imageType
                    ) {
                        type
                        image {
                            url
                        }
                    }
                    shortDescription
                }
            }
            contentSeries(language: $language) {
                id
                contentAnalytic @skip(if: $limitedData) {
                    commentsCountTotal
                    votesUpCountTotal
                    votesCountTotal
                    thumbPopularityMappingId
                }
                contentId
                title
                studio @skip(if: $limitedData)
                cast @skip(if: $limitedData)
                path
                instructor @skip(if: $limitedData)
                description (format: HTML) @skip(if: $limitedData)
                shortDescription
                episodesCount (language: $primaryLanguage, content: [{ publishedState: PUBLISHED_NOW }]) @skip(if: $limitedData)
                seasonsCount (language: $primaryLanguage) @skip(if: $limitedData)
                contentType @skip(if: $limitedData)
                subType @skip(if: $limitedData)
                displayType @skip(if: $limitedData)
                imageContentRelationships (
                    language: $language
                    types: $imageType
                ) @include(if: $includeSeriesImage) {
                    type
                    image {
                        url
                    }
                }
                contentFeatured (language: $language, content: [{ publishedState: PUBLISHED_NOW }]) @skip(if: $limitedData) {
                    content {
                        id
                    }
                    featuredType
                }
                previewMedia @skip(if: $limitedData) {
                    mediaId
                }
                contentAssetReferences (contentAssetReferences: {type: "worksheet"}) @skip(if: $limitedData) {
                    asset {
                        originalFileName
                        language
                        renderUrl
                        mimeType
                        uuid
                    }
                    contentAssetReferenceType {
                        type
                    }
                }
                contentReferencesFrom (types: ["product"]) @skip(if: $limitedData) {
                    contentTo {
                        contentId
                        contentType
                        title
                        ctaProductUrl
                        ctaLabel
                        imageContentRelationships (
                            language: $language
                            types: $imageType
                        ) {
                            type
                            image {
                                url
                            }
                        }
                    }
                }
                vote @skip(if: $limitedData) {
                    id
                    voteDirection
            }
            }
            contentFeatured (language: $primaryLanguage, content: [{ publishedState: PUBLISHED_NOW }]) {
                content {
                    contentAnalytic {
                        commentsCountTotal
                        votesUpCountTotal
                        votesCountTotal
                        thumbPopularityMappingId
                    }
                    id
                    title
                    path
                    ctaProductUrl
                    episodeNumber
                    seasonNumber
                    displayType
                    contentType
                    subType
                    contentId
                    isNew
                    primaryLanguage
                    languageFullText
                    shortDescription
                    description (format: HTML) @skip(if: $limitedData)
                    cast
                    instructor
                    references (types: ["genres", "centers", "yoga-duration", "yoga-style", "yoga-level", "fitness-duration", "fitness-style", "fitness-level", "meditation-duration", "meditation-style", "meditation-level"]) {
                        id
                        contentReferenceType {
                            type
                        }
                        contentTo (language: $language) {
                            title
                            contentType
                            contentId
                            language
                            path
                        }
                    }
                    featureMedia {
                        duration
                        mediaId
                        mediaAssets {
                            language
                            mediaReferenceType
                            aiMetadata(language: $learnerLevelLanguage) {
                                learnerLevel {
                                    level
                                }
                            }
                        }
                        contentPolicy {
                            shareAllowed
                            accessCheck {
                                allowed
                                countryAllowed
                                subscriptionsAllowed
                                isFreeAllowed
                            }
                            optionIds
                        }
                        userMediaPosition {
                            position
                        }
                    }
                    previewMedia {
                        mediaId
                    }
                    contentAssetReferences (contentAssetReferences: {type: "worksheet"}) @skip(if: $limitedData) {
                        asset {
                            originalFileName
                            language
                            renderUrl
                            mimeType
                            uuid
                        }
                        contentAssetReferenceType {
                            type
                        }
                    }
                    contentReferencesFrom (types: ["product"]) @skip(if: $limitedData) {
                        contentTo {
                            id
                            contentId
                            contentType
                            title
                            ctaProductUrl
                            ctaLabel
                            imageContentRelationships (
                                language: $language
                                types: $imageType
                            ) {
                                type
                                image {
                                    url
                                }
                            }
                        }
                    }
                    contentCourses (language: $primaryLanguage, content: [{ publishedState: PUBLISHED_NOW }]) {
                        id
                        contentId
                        titleGroupId
                    }
                }
                featuredType
            }
            vote @skip(if: $limitedData) {
                id
                voteDirection
            }
            courseProgress @include(if: $includeCourseProgress) {
                state
                completionCount
                    metrics {
                        totalModules
                        completedModules
                        totalItems
                        completedItems
                        percentageModulesComplete
                        percentageItemsComplete
                    }
            }
            contentCourses (language: $primaryLanguage, content: [{ publishedState: PUBLISHED_NOW }]) {
                id
                contentId
                titleGroupId
            }
        }
    }
}
`

/** @deprecated Import from `services/detail/query` — canonical GetEpisodes document. */
export { GET_EPISODES } from '../detail/query'

export const GET_RELATED = gql`
  query GetContent ($titleGroupIds: [String]!, $language: [String]!, $imageType: [String]!, $limit: Int!) {
    contentList(
        where: {
            titleGroupId: { in: $titleGroupIds }
        }
        language: $language
    ) {
        content {
            id
            contentRelated (language: $language, limit: $limit, content: [{ publishedState: PUBLISHED_NOW }]) {
                contentAnalytic {
                    votesUpCountTotal
                    votesCountTotal
                    commentsCountTotal
                    thumbPopularityMappingId
                }
                id
                title
                path
                episodeNumber
                seasonNumber
                displayType
                studio
                contentType
                subType
                contentId
                titleGroupId
                seasonsCount
                episodesCount (content: [{ publishedState: PUBLISHED_NOW }])
                isNew
                primaryLanguage
                languageFullText
                cast
                instructor
                published
                shortDescription
                references (types: ["genres", "centers", "yoga-duration", "yoga-style", "yoga-level", "fitness-duration", "fitness-style", "fitness-level", "meditation-duration", "meditation-style", "meditation-level", "primary-course"]) {
                    id
                    contentReferenceType {
                        type
                    }
                    contentTo (language: $language) {
                        title
                        contentType
                        contentId
                        language
                        path
                    }
                }
                featureMedia {
                    duration
                    mediaId
                    mediaAssets {
                        language
                        mediaReferenceType
                    }
                    contentPolicy {
                        shareAllowed
                        accessCheck {
                            allowed
                            countryAllowed
                            subscriptionsAllowed
                            isFreeAllowed
                        }
                        optionIds
                    }
                    userMediaPosition {
                        position
                    }
                }
                previewMedia {
                    mediaId
                }
                imageContentRelationships (
                    language: $language
                    types: $imageType
                ) {
                    type
                    image {
                        url
                    }
                }
                contentSeries(language: $language) {
                    id
                    contentId
                    title
                    path
                }
                contentFeatured (language: $language, content: [{ publishedState: PUBLISHED_NOW }]) {
                    content {
                        contentAnalytic {
                            votesUpCountTotal
                            votesCountTotal
                            commentsCountTotal
                            thumbPopularityMappingId
                        }
                        id
                        title
                        path
                        ctaProductUrl
                        episodeNumber
                        seasonNumber
                        displayType
                        contentType
                        subType
                        contentId
                        isNew
                        primaryLanguage
                        languageFullText
                        shortDescription
                        cast
                        instructor
                        references (types: ["genres", "centers", "yoga-duration", "yoga-style", "yoga-level", "fitness-duration", "fitness-style", "fitness-level", "meditation-duration", "meditation-style", "meditation-level"]) {
                            id
                            contentReferenceType {
                                type
                            }
                            contentTo (language: $language) {
                                title
                                contentType
                                contentId
                                language
                                path
                            }
                        }
                        featureMedia {
                            duration
                            mediaId
                            mediaAssets {
                                language
                                mediaReferenceType
                            }
                            contentPolicy {
                                shareAllowed
                                accessCheck {
                                    allowed
                                    countryAllowed
                                    subscriptionsAllowed
                                    isFreeAllowed
                                }
                                optionIds
                            }
                            userMediaPosition {
                                position
                            }
                        }
                        previewMedia {
                            mediaId
                        }
                    }
                    featuredType
                }
            }
        }
    }
}
`

export const GET_RECOMMENDED = gql`
  query GetRecommended ($scenario: String!, $language: String!, $contentLanguages: [String]!, $imageType: [String]!, $limit: Int!) {
    recommendationList(
        scenario: $scenario
        language: $language
        limit: $limit
    ) {
        items {
            id
            contentAnalytic {
                votesUpCountTotal
                votesCountTotal
                commentsCountTotal
                thumbPopularityMappingId
            }
            id
            title
            path
            episodeNumber
            seasonNumber
            displayType
            studio
            contentType
            subType
            contentId
            titleGroupId
            seasonsCount
            episodesCount (content: [{ publishedState: PUBLISHED_NOW }])
            isNew
            primaryLanguage
            languageFullText
            cast
            instructor
            published
            shortDescription
            references (types: ["genres", "centers", "yoga-duration", "yoga-style", "yoga-level", "fitness-duration", "fitness-style", "fitness-level", "meditation-duration", "meditation-style", "meditation-level", "primary-course"]) {
                id
                contentReferenceType {
                    type
                }
                contentTo (language: $contentLanguages) {
                    title
                    contentType
                    contentId
                    language
                    path
                }
            }
            featureMedia {
                duration
                mediaId
                mediaAssets {
                    language
                    mediaReferenceType
                }
                contentPolicy {
                    shareAllowed
                    accessCheck {
                        allowed
                        countryAllowed
                        subscriptionsAllowed
                        isFreeAllowed
                    }
                    optionIds
                }
                userMediaPosition {
                    position
                }
            }
            previewMedia {
                mediaId
            }
            imageContentRelationships (
                language: $contentLanguages
                types: $imageType
            ) {
                type
                image {
                    url
                }
            }
            contentSeries(language: $contentLanguages) {
                id
                contentId
                title
                path
            }
            contentFeatured (language: $contentLanguages, content: [{ publishedState: PUBLISHED_NOW }]) {
                content {
                    contentAnalytic {
                        votesUpCountTotal
                        votesCountTotal
                        commentsCountTotal
                        thumbPopularityMappingId
                    }
                    id
                    title
                    path
                    ctaProductUrl
                    episodeNumber
                    seasonNumber
                    displayType
                    contentType
                    subType
                    contentId
                    isNew
                    primaryLanguage
                    languageFullText
                    shortDescription
                    cast
                    instructor
                    references (types: ["genres", "centers", "yoga-duration", "yoga-style", "yoga-level", "fitness-duration", "fitness-style", "fitness-level", "meditation-duration", "meditation-style", "meditation-level"]) {
                        id
                        contentReferenceType {
                            type
                        }
                        contentTo (language: $contentLanguages) {
                            title
                            contentType
                            contentId
                            language
                            path
                        }
                    }
                    featureMedia {
                        duration
                        mediaId
                        mediaAssets {
                            language
                            mediaReferenceType
                        }
                        contentPolicy {
                            shareAllowed
                            accessCheck {
                                allowed
                                countryAllowed
                                subscriptionsAllowed
                                isFreeAllowed
                            }
                            optionIds
                        }
                        userMediaPosition {
                            position
                        }
                    }
                    previewMedia {
                        mediaId
                    }
                }
                featuredType
            }
        }
    }
}
`

export const GET_FAQS = gql`
  query GetFaqs ($titleGroupIds: [String]!, $language: [String]!) {
    contentList(
        where: {
            titleGroupId: { in: $titleGroupIds }
        }
        language: $language
    ) {
        content {
            id
            title
            shortDescription
            description
            displayType
            contentType
            contentId
            titleGroupId
            primaryLanguage
        }
    }
}
`

export const GET_PERSONS = gql`
  query GetPersons ($titleGroupIds: [String]!, $language: [String]!, $imageType: [String]!) {
    contentList(
        where: {
            titleGroupId: { in: $titleGroupIds }
        }
        language: $language
    ) {
        content {
            id
            title
            description
            shortDescription
            path
            displayType
            contentType
            subType
            contentId
            titleGroupId
            primaryLanguage
            references (types: ["genres", "centers", "yoga-duration", "yoga-style", "yoga-level", "fitness-duration", "fitness-style", "fitness-level", "meditation-style", "meditation-level"]) {
                id
                contentReferenceType {
                    type
                }
                contentTo (language: $language) {
                    title
                    contentType
                    contentId
                    language
                    path
                }
            }
            imageContentRelationships (
                language: $language
                types: $imageType
            ) {
                type
                image {
                    url
                }
            }
        }
    }
}
`
